import * as React from 'react';
import { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Slider,
  Tabs,
  Tab,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Tooltip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Autocomplete,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import ReactMarkdown from 'react-markdown';
import {
  Add as AddIcon,
  Close as CloseIcon,
  Download as DownloadIcon,
  ContentCopy as CopyIcon,
  Delete as DeleteIcon,
  History as HistoryIcon,
  Article as ArticleIcon,
  FactCheck as FactCheckIcon,
} from '@mui/icons-material';
import { JournalismService } from '../services/journalismService';
import {
  JournalismSession,
  JournalismSessionStorage,
  MultiJournalismRequest,
  JOURNALISM_STYLES,
  TONE_OPTIONS,
  PERSPECTIVE_OPTIONS
} from '../models/journalism';
import { useAssetRetrieval } from '../hooks/useAssetRetrieval';
import { SessionWithAssets } from '../services/assetRetrievalService';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`journalism-tabpanel-${index}`}
      aria-labelledby={`journalism-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: { xs: 2, sm: 3 } }}>{children}</Box>}
    </div>
  );
}

const JournalismMultiAgent: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const [tabValue, setTabValue] = useState(0);
  const [topic, setTopic] = useState('');
  const [style, setStyle] = useState('');
  const [toneGuidance, setToneGuidance] = useState('');
  const [keyFacts, setKeyFacts] = useState<string[]>([]);
  const [currentFact, setCurrentFact] = useState('');
  const [perspective, setPerspective] = useState('');
  const [iterations, setIterations] = useState(3);
  const [maxTokens, setMaxTokens] = useState(3072);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [finalArticle, setFinalArticle] = useState('');
  const [sessions, setSessions] = useState<JournalismSession[]>([]);

  // Asset retrieval hook for backend data
  const { sessions: backendSessions, loading: backendLoading, refetch: refetchBackendData } = useAssetRetrieval({
    agentType: 'JournalismMultiAgentSession',  // Fixed: Use SessionType not agent name
    sessionBased: true,
    autoFetch: true
  });

  // Merge backend sessions with local sessions
  useEffect(() => {
    const stored = localStorage.getItem('journalismSessions');
    let localSessions: JournalismSession[] = [];

    if (stored) {
      const data: JournalismSessionStorage = JSON.parse(stored);
      localSessions = data.sessions.map(s => ({
        ...s,
        createdAt: new Date(s.createdAt)
      }));
    }

    // Convert backend sessions to JournalismSession format
    const convertedBackendSessions: JournalismSession[] = backendSessions.map((backendSession: SessionWithAssets) => {
      // Parse sessionData JSON string to extract journalism data
      let sessionData: any = {};
      try {
        if (backendSession.session.sessionData) {
          sessionData = JSON.parse(backendSession.session.sessionData);
        }
      } catch (e) {
        console.error('[JournalismMultiAgent] Failed to parse sessionData:', e);
      }

      return {
        id: backendSession.session.id,
        topic: sessionData.Topic || backendSession.session.metadata?.topic || 'Untitled Article',
        style: sessionData.Style || backendSession.session.style || backendSession.session.metadata?.style,
        toneGuidance: sessionData.ToneGuidance || backendSession.session.metadata?.toneGuidance,
        keyFacts: backendSession.session.metadata?.keyFacts,
        perspective: sessionData.Perspective || backendSession.session.metadata?.perspective,
        finalArticle: sessionData.FinalArticle || backendSession.assets.find(a => a.assetType === 'article')?.textContent || '',
        researchNotes: sessionData.ResearchNotes,
        createdAt: new Date(backendSession.session.createdAt),
        iterations: backendSession.session.metadata?.iterations || 3
      };
    });

    // Merge: backend sessions + local-only sessions (filter out duplicates by id)
    const backendIds = new Set(convertedBackendSessions.map(s => s.id));
    const uniqueLocalSessions = localSessions.filter(s => !backendIds.has(s.id));
    const merged = [...convertedBackendSessions, ...uniqueLocalSessions].sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );

    setSessions(merged);
  }, [backendSessions]);

  // Save sessions to localStorage
  const saveSessions = (newSessions: JournalismSession[]) => {
    const storage: JournalismSessionStorage = {
      sessions: newSessions,
      lastUpdated: new Date()
    };
    localStorage.setItem('journalismSessions', JSON.stringify(storage));
    setSessions(newSessions);
  };

  const handleAddFact = () => {
    if (currentFact.trim()) {
      setKeyFacts([...keyFacts, currentFact.trim()]);
      setCurrentFact('');
    }
  };

  const handleRemoveFact = (index: number) => {
    setKeyFacts(keyFacts.filter((_, i) => i !== index));
  };

  const handleGenerate = async () => {
    if (!topic.trim()) {
      setError('Please enter a topic for the article');
      return;
    }

    setLoading(true);
    setError(null);
    setFinalArticle('');

    try {
      const request: MultiJournalismRequest = {
        topic: topic.trim(),
        style: style || undefined,
        toneGuidance: toneGuidance || undefined,
        keyFacts: keyFacts.length > 0 ? keyFacts : undefined,
        perspective: perspective || undefined,
        iterations,
        maxTokensPerAgentTurn: maxTokens
      };

      const response = await JournalismService.generateArticle(request);
      
      if (response.finalArticle) {
        setFinalArticle(response.finalArticle);
        
        // Save to sessions
        const newSession: JournalismSession = {
          id: Date.now().toString(),
          topic: topic.trim(),
          style: style || undefined,
          toneGuidance: toneGuidance || undefined,
          keyFacts: keyFacts.length > 0 ? keyFacts : undefined,
          perspective: perspective || undefined,
          finalArticle: response.finalArticle,
          createdAt: new Date(),
          iterations
        };

        saveSessions([newSession, ...sessions]);
        await refetchBackendData(); // Refresh backend data
        setTabValue(1); // Switch to results tab
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate article');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const handleDownload = (content: string, filename: string) => {
    const blob = new Blob([content], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleLoadSession = (session: JournalismSession) => {
    setTopic(session.topic);
    setStyle(session.style || '');
    setToneGuidance(session.toneGuidance || '');
    setKeyFacts(session.keyFacts || []);
    setPerspective(session.perspective || '');
    setFinalArticle(session.finalArticle || '');
    setIterations(session.iterations || 3);
    setTabValue(1); // Switch to results tab
  };

  const handleDeleteSession = (sessionId: string) => {
    saveSessions(sessions.filter(s => s.id !== sessionId));
  };

  const getWordCount = (text: string) => {
    return text.trim().split(/\s+/).length;
  };

  return (
    <Container maxWidth="md" sx={{ py: { xs: 2, sm: 3, md: 4 }, px: { xs: 1, sm: 2, md: 3 } }}>
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: { xs: 2, sm: 3, md: 4 } }}>
        <Typography 
          variant="h4" 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            gap: 1, 
            mb: { xs: 1, sm: 2 },
            fontSize: { xs: '1.5rem', sm: '1.875rem', md: '2.125rem' }
          }}
        >
          <ArticleIcon sx={{ fontSize: { xs: 32, sm: 40 } }} />
          {isMobile ? 'Journalism Agent' : 'Journalism Multi-Agent'}
        </Typography>
        <Typography 
          variant="body1" 
          color="text.secondary"
          sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
        >
          {isMobile ? 'Create articles with AI agents' : 'Create professional journalism articles with AI agents specializing in research, writing, and editing'}
        </Typography>
      </Box>

      {/* Tab Navigation */}
      <Paper sx={{ mb: { xs: 2, sm: 3 } }}>
        <Tabs 
          value={tabValue} 
          onChange={(_, newValue) => setTabValue(newValue)} 
          variant="fullWidth"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab 
            label="Generate" 
            sx={{ 
              textTransform: 'none',
              minHeight: 48,
              fontSize: { xs: '0.875rem', sm: '1rem' }
            }} 
          />
          <Tab 
            label="Article" 
            sx={{ 
              textTransform: 'none',
              minHeight: 48,
              fontSize: { xs: '0.875rem', sm: '1rem' }
            }} 
          />
          <Tab 
            label="History" 
            sx={{ 
              textTransform: 'none',
              minHeight: 48,
              fontSize: { xs: '0.875rem', sm: '1rem' }
            }} 
          />
        </Tabs>
      </Paper>

        <TabPanel value={tabValue} index={0}>
          <Paper sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
            <Typography 
              variant="h5" 
              gutterBottom 
              sx={{ 
                mb: { xs: 2, sm: 3 },
                fontSize: { xs: '1.25rem', sm: '1.5rem' }
              }}
            >
              {isMobile ? 'Configuration' : 'Article Configuration'}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2, sm: 3 } }}>
            <TextField
              fullWidth
              label={isMobile ? 'Topic' : 'Article Topic'}
              placeholder={isMobile ? 'e.g., Climate Change' : 'e.g., Climate Change Impact on Urban Planning, Tech Industry Layoffs 2024'}
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              required
              helperText={isMobile ? undefined : 'The main topic or headline for your article'}
              InputProps={{
                sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
              }}
              InputLabelProps={{
                sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
              }}
            />

            <FormControl fullWidth>
              <InputLabel sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                {isMobile ? 'Style' : 'Article Style'}
              </InputLabel>
              <Select
                value={style}
                onChange={(e) => setStyle(e.target.value)}
                label={isMobile ? 'Style' : 'Article Style'}
                sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
              >
                <MenuItem value="" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>None</MenuItem>
                {JOURNALISM_STYLES.map((s) => (
                  <MenuItem 
                    key={s} 
                    value={s}
                    sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                  >
                    {s}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                {isMobile ? 'Tone' : 'Tone Guidance'}
              </InputLabel>
              <Select
                value={toneGuidance}
                onChange={(e) => setToneGuidance(e.target.value)}
                label={isMobile ? 'Tone' : 'Tone Guidance'}
                sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
              >
                <MenuItem value="" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>None</MenuItem>
                {TONE_OPTIONS.map((tone) => (
                  <MenuItem 
                    key={tone} 
                    value={tone}
                    sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                  >
                    {tone}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Perspective</InputLabel>
              <Select
                value={perspective}
                onChange={(e) => setPerspective(e.target.value)}
                label="Perspective"
                sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
              >
                <MenuItem value="" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>None</MenuItem>
                {PERSPECTIVE_OPTIONS.map((p) => (
                  <MenuItem 
                    key={p} 
                    value={p}
                    sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                  >
                    {p}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box>
              <Typography 
                variant="subtitle1" 
                gutterBottom 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1,
                  fontSize: { xs: '1rem', sm: '1.125rem' }
                }}
              >
                <FactCheckIcon sx={{ fontSize: { xs: 20, sm: 24 } }} /> 
                {isMobile ? 'Key Facts' : 'Key Facts & Information'}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder={isMobile ? 'Add fact' : 'Add a key fact or important information'}
                  value={currentFact}
                  onChange={(e) => setCurrentFact(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleAddFact();
                    }
                  }}
                  InputProps={{
                    sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                  }}
                />
                <Button
                  variant="outlined"
                  onClick={handleAddFact}
                  startIcon={!isMobile && <AddIcon />}
                  sx={{
                    minHeight: 44,
                    minWidth: { xs: 60, sm: 80 }
                  }}
                >
                  Add
                </Button>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {keyFacts.map((fact, index) => (
                  <Chip
                    key={index}
                    label={fact}
                    onDelete={() => handleRemoveFact(index)}
                    deleteIcon={<CloseIcon />}
                    variant="outlined"
                    sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                  />
                ))}
              </Box>
              {keyFacts.length === 0 && (
                <Typography 
                  variant="body2" 
                  color="text.secondary" 
                  sx={{ 
                    mt: 1,
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  }}
                >
                  {isMobile ? 'Add facts to include' : 'Add important facts, statistics, or information that should be included in the article'}
                </Typography>
              )}
            </Box>

            <Box>
              <Typography 
                gutterBottom
                sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
              >
                {isMobile ? 'Iterations' : 'Agent Iterations'}: {iterations}
              </Typography>
              <Slider
                value={iterations}
                onChange={(_, value) => setIterations(value as number)}
                min={1}
                max={5}
                marks
                valueLabelDisplay="auto"
              />
            </Box>

            <Box>
              <Typography 
                gutterBottom
                sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
              >
                {isMobile ? 'Tokens' : 'Max Tokens per Agent'}: {maxTokens}
              </Typography>
              <Slider
                value={maxTokens}
                onChange={(_, value) => setMaxTokens(value as number)}
                min={1024}
                max={8192}
                step={512}
                valueLabelDisplay="auto"
                marks={[
                  { value: 1024, label: '1K' },
                  { value: 3072, label: '3K' },
                  { value: 5120, label: '5K' },
                  { value: 8192, label: '8K' },
                ]}
              />
            </Box>

            {error && (
              <Alert 
                severity="error" 
                onClose={() => setError(null)}
                sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
              >
                {error}
              </Alert>
            )}

            <Button
              variant="contained"
              size={isMobile ? 'medium' : 'large'}
              fullWidth
              onClick={handleGenerate}
              disabled={loading || !topic.trim()}
              startIcon={loading ? <CircularProgress size={20} /> : (!isMobile && <ArticleIcon />)}
              sx={{ 
                mt: 2,
                minHeight: 44,
                fontSize: { xs: '0.875rem', sm: '1rem' }
              }}
            >
              {loading ? (isMobile ? 'Generating...' : 'Generating Article...') : (isMobile ? 'Generate' : 'Generate Article')}
            </Button>
            </Box>
          </Paper>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          {finalArticle ? (
            <Paper sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
              <Box sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between', 
                alignItems: { xs: 'flex-start', sm: 'center' }, 
                mb: { xs: 2, sm: 3 },
                gap: { xs: 1, sm: 0 }
              }}>
                <Box>
                  <Typography 
                    variant="h5" 
                    sx={{ 
                      mb: 1,
                      fontSize: { xs: '1.25rem', sm: '1.5rem' }
                    }}
                  >
                    {topic}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    color="text.secondary"
                    sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                  >
                    {isMobile ? (
                      `${getWordCount(finalArticle)} words`
                    ) : (
                      <>
                        {style && `${style} • `}
                        {toneGuidance && `${toneGuidance} tone • `}
                        {getWordCount(finalArticle)} words
                      </>
                    )}
                  </Typography>
                </Box>
                <Box>
                  <Tooltip title={isMobile ? '' : 'Copy to clipboard'}>
                    <IconButton 
                      onClick={() => handleCopy(finalArticle)} 
                      size="small"
                      sx={{ minWidth: 44, minHeight: 44 }}
                    >
                      <CopyIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={isMobile ? '' : 'Download as Markdown'}>
                    <IconButton 
                      onClick={() => handleDownload(
                        finalArticle, 
                        `${topic.replace(/\s+/g, '-').toLowerCase()}-article.md`
                      )} 
                      size="small"
                      sx={{ minWidth: 44, minHeight: 44 }}
                    >
                      <DownloadIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
              
              <Box
                sx={{
                  backgroundColor: (theme) => theme.palette.mode === 'light' ? 'background.default' : 'background.paper',
                  border: (theme) => `1px solid ${theme.palette.divider}`,
                  borderRadius: 2,
                  p: { xs: 2, sm: 3, md: 4 },
                  overflow: 'auto',
                  maxHeight: { xs: '50vh', sm: '70vh' }
                }}
              >
                <Box
                  sx={{
                    fontFamily: 'Georgia, "Times New Roman", serif',
                    fontSize: { xs: '0.95rem', sm: '1.1rem' },
                    lineHeight: 1.8,
                    color: 'text.primary',
                    '& h1, & h2, & h3, & h4, & h5, & h6': {
                      fontWeight: 'bold',
                      marginBottom: 2,
                      marginTop: 3
                    },
                    '& p': {
                      marginBottom: 2
                    },
                    '& ul, & ol': {
                      marginBottom: 2,
                      paddingLeft: 3
                    },
                    '& li': {
                      marginBottom: 1
                    },
                    '& blockquote': {
                      borderLeft: '4px solid',
                      borderColor: 'primary.main',
                      paddingLeft: 2,
                      marginY: 2,
                      fontStyle: 'italic'
                    },
                    '& code': {
                      backgroundColor: 'action.hover',
                      padding: '2px 6px',
                      borderRadius: 1,
                      fontSize: '0.9em'
                    },
                    '& pre': {
                      backgroundColor: 'action.hover',
                      padding: 2,
                      borderRadius: 1,
                      overflow: 'auto',
                      marginY: 2
                    }
                  }}
                >
                  <ReactMarkdown>{finalArticle}</ReactMarkdown>
                </Box>
              </Box>
            </Paper>
          ) : (
            <Paper sx={{ p: { xs: 4, sm: 6 }, textAlign: 'center' }}>
              <ArticleIcon sx={{ 
                fontSize: { xs: 60, sm: 80 }, 
                color: 'text.secondary', 
                mb: 2 
              }} />
              <Typography 
                variant="h6" 
                color="text.secondary" 
                gutterBottom
                sx={{ fontSize: { xs: '1rem', sm: '1.125rem' } }}
              >
                {isMobile ? 'No article yet' : 'No article generated yet'}
              </Typography>
              <Typography 
                variant="body2" 
                color="text.secondary" 
                sx={{ 
                  mb: 3,
                  fontSize: { xs: '0.875rem', sm: '1rem' }
                }}
              >
                {isMobile ? 'Go to Generate tab' : 'Configure your article settings and generate content to see results here'}
              </Typography>
              <Button
                variant="outlined"
                onClick={() => setTabValue(0)}
                startIcon={!isMobile && <ArticleIcon />}
                sx={{
                  minHeight: 44,
                  fontSize: { xs: '0.875rem', sm: '1rem' }
                }}
              >
                {isMobile ? 'Generate' : 'Go to Generate'}
              </Button>
            </Paper>
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          {sessions.length > 0 ? (
            <Paper sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
              <Typography 
                variant="h5" 
                gutterBottom 
                sx={{ 
                  mb: { xs: 2, sm: 3 },
                  fontSize: { xs: '1.25rem', sm: '1.5rem' }
                }}
              >
                {isMobile ? `History (${sessions.length})` : `Article History (${sessions.length})`}
              </Typography>
              <List sx={{ bgcolor: 'background.paper' }}>
                {sessions.map((session) => (
                  <ListItem 
                    key={session.id} 
                    divider
                    sx={{ 
                      cursor: 'pointer',
                      borderRadius: 1,
                      mb: 1,
                      py: { xs: 1.5, sm: 2 },
                      '&:hover': { 
                        backgroundColor: 'action.hover',
                        borderRadius: 1
                      }
                    }}
                    onClick={() => handleLoadSession(session)}
                  >
                    <ListItemText
                      primary={
                        <Typography 
                          variant="h6" 
                          sx={{ 
                            mb: 0.5,
                            fontSize: { xs: '1rem', sm: '1.125rem' }
                          }}
                        >
                          {session.topic}
                        </Typography>
                      }
                      secondary={
                        <Box>
                          <Typography 
                            variant="body2" 
                            color="text.secondary"
                            sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                          >
                            {isMobile ? (
                              `${getWordCount(session.finalArticle || '')} words`
                            ) : (
                              <>
                                {session.style && `${session.style} • `}
                                {session.toneGuidance && `${session.toneGuidance} • `}
                                {session.perspective && `${session.perspective} • `}
                                {session.keyFacts && `${session.keyFacts.length} facts • `}
                                {getWordCount(session.finalArticle || '')} words
                              </>
                            )}
                          </Typography>
                          <Typography 
                            variant="caption" 
                            color="text.secondary"
                            sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                          >
                            {isMobile ? session.createdAt.toLocaleDateString() : `Created: ${session.createdAt.toLocaleDateString()}`}
                          </Typography>
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      <Tooltip title={isMobile ? '' : 'Delete'}>
                        <IconButton 
                          edge="end" 
                          onClick={(e) => {
                            e.stopPropagation();
                            if (window.confirm('Delete this article?')) {
                              handleDeleteSession(session.id);
                            }
                          }}
                          sx={{ minWidth: 44, minHeight: 44 }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </Paper>
          ) : (
            <Paper sx={{ p: { xs: 4, sm: 6 }, textAlign: 'center' }}>
              <HistoryIcon sx={{ 
                fontSize: { xs: 60, sm: 80 }, 
                color: 'text.secondary', 
                mb: 2 
              }} />
              <Typography 
                variant="h6" 
                color="text.secondary" 
                gutterBottom
                sx={{ fontSize: { xs: '1rem', sm: '1.125rem' } }}
              >
                {isMobile ? 'No history' : 'No articles in history'}
              </Typography>
              <Typography 
                variant="body2" 
                color="text.secondary" 
                sx={{ 
                  mb: 3,
                  fontSize: { xs: '0.875rem', sm: '1rem' }
                }}
              >
                {isMobile ? 'Articles will appear here' : 'Generated articles will appear here for easy access and management'}
              </Typography>
              <Button
                variant="outlined"
                onClick={() => setTabValue(0)}
                startIcon={!isMobile && <ArticleIcon />}
                sx={{
                  minHeight: 44,
                  fontSize: { xs: '0.875rem', sm: '1rem' }
                }}
              >
                {isMobile ? 'Create' : 'Create Article'}
              </Button>
            </Paper>
          )}
        </TabPanel>
    </Container>
  );
};

export default JournalismMultiAgent;